import { useRef, useState } from 'react';
import { ChevronDown, Lock } from 'lucide-react';
import AnchoredPortal from './AnchoredPortal';
import { roleColor, isOwnerRole, isRoleAssignable } from './constants';

// Coluna "Grupo" — badge de status com dropdown, no mesmo espírito visual do
// StatusCell do quadro (frontend/src/components/board/cells/StatusCell.jsx).
// Estado de salvando/erro é local à célula (nunca global — §6.10).
export default function RoleCell({ user, role, roles, canEdit, onChange }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const btnRef = useRef(null);
  const color = roleColor(role);
  const locked = isOwnerRole(role);
  const disabled = !canEdit || locked || saving;

  const handlePick = async (r) => {
    setOpen(false);
    if (r.id === role?.id) return;
    setSaving(true);
    setError(null);
    try {
      await onChange(r);
    } catch (err) {
      setError(err.message || 'Falha ao trocar o grupo');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="relative">
      <button
        ref={btnRef}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        title={error || (locked ? 'Papel do Proprietário — não pode ser alterado' : role?.name)}
        className={`inline-flex items-center gap-1.5 rounded px-2 py-1 text-xs font-semibold text-white max-w-full transition-opacity ${
          !disabled ? 'hover:opacity-90 cursor-pointer' : 'cursor-default'
        } ${saving ? 'opacity-60' : ''} ${error ? 'ring-1 ring-danger' : ''}`}
        style={{ backgroundColor: color }}
      >
        {locked && <Lock className="w-3 h-3 shrink-0" />}
        <span className="truncate">{role?.name || 'Sem grupo'}</span>
        {!disabled && <ChevronDown className="w-3 h-3 opacity-80 shrink-0" />}
      </button>

      {error && (
        <button
          type="button"
          onClick={() => setError(null)}
          className="block text-[10px] text-danger mt-0.5 text-left hover:underline"
        >
          {error} · tentar de novo
        </button>
      )}

      {open && (
        <AnchoredPortal anchorRef={btnRef} onClose={() => setOpen(false)} width={220}>
          <div className="py-1 max-h-72 overflow-y-auto">
            {roles.filter((r) => !isOwnerRole(r)).map((r) => {
              const assignable = isRoleAssignable(r);
              return (
                <button
                  key={r.id}
                  disabled={!assignable}
                  onClick={() => handlePick(r)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-text-primary hover:bg-white/5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: roleColor(r) }} />
                  <span className="truncate">{r.name}{!assignable ? ' (em breve)' : ''}</span>
                </button>
              );
            })}
          </div>
        </AnchoredPortal>
      )}
    </div>
  );
}
